/**
 * Gear Designer for CoandaView CAD
 *
 * Provides UI for creating parametric gears:
 * - Spur gears (straight teeth)
 * - Helical gears (angled teeth)
 * - Internal gears (ring gears)
 * - Rack gears (linear)
 * - Bevel gears (conical)
 * - Worm gears (screw-like)
 *
 * Integrates with CADViewer and geometry service for mesh generation.
 */

class GearDesigner {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.viewer = cadViewer.viewer;

    // Current gear being designed
    this.currentGearType = null;
    this.currentParameters = {};

    // UI elements
    this.modal = null;

    // Gear defaults
    this.defaults = {
      spur: {
        module: 2.0,
        teeth: 20,
        pressure_angle: 20.0,
        width: 10.0
      },
      helical: {
        module: 2.0,
        teeth: 20,
        helix_angle: 15.0,
        pressure_angle: 20.0,
        width: 10.0
      },
      internal: {
        module: 2.0,
        teeth: 30,
        pressure_angle: 20.0,
        width: 10.0
      },
      rack: {
        module: 2.0,
        teeth: 10,
        pressure_angle: 20.0,
        width: 10.0,
        height: 6.0
      },
      bevel: {
        module: 2.0,
        teeth: 20,
        cone_angle: 45.0,
        pressure_angle: 20.0,
        width: 10.0
      },
      worm: {
        module: 2.0,
        starts: 1,
        diameter: 20.0,
        length: 50.0
      }
    };

    this.initialize();
  }

  /**
   * Initialize gear designer
   */
  initialize() {
    console.log('[GearDesigner] Initializing...');

    // Wire up gear buttons
    this.wireUpButtons();

    console.log('[GearDesigner] Ready');
  }

  /**
   * Wire up gear button event handlers
   */
  wireUpButtons() {
    // Spur gear
    const btnSpur = document.getElementById('cadGearSpur');
    if (btnSpur) {
      btnSpur.addEventListener('click', () => this.openGearDialog('spur'));
    }

    // Helical gear
    const btnHelical = document.getElementById('cadGearHelical');
    if (btnHelical) {
      btnHelical.addEventListener('click', () => this.openGearDialog('helical'));
    }

    // Internal gear
    const btnInternal = document.getElementById('cadGearInternal');
    if (btnInternal) {
      btnInternal.addEventListener('click', () => this.openGearDialog('internal'));
    }

    // Rack gear
    const btnRack = document.getElementById('cadGearRack');
    if (btnRack) {
      btnRack.addEventListener('click', () => this.openGearDialog('rack'));
    }

    // Bevel gear
    const btnBevel = document.getElementById('cadGearBevel');
    if (btnBevel) {
      btnBevel.addEventListener('click', () => this.openGearDialog('bevel'));
    }

    // Worm gear
    const btnWorm = document.getElementById('cadGearWorm');
    if (btnWorm) {
      btnWorm.addEventListener('click', () => this.openGearDialog('worm'));
    }
  }

  /**
   * Open gear parameter dialog
   */
  openGearDialog(gearType) {
    this.currentGearType = gearType;
    this.currentParameters = { ...this.defaults[gearType] };

    // Create modal
    this.createModal(gearType);
  }

  /**
   * Create parameter input modal
   */
  createModal(gearType) {
    // Remove existing modal if present
    if (this.modal) {
      this.modal.remove();
    }

    // Create modal container
    this.modal = document.createElement('div');
    this.modal.className = 'cad-modal';
    this.modal.innerHTML = `
      <div class="cad-modal-content">
        <div class="cad-modal-header">
          <h3>${this.getGearTypeName(gearType)} Parameters</h3>
          <button class="cad-modal-close">&times;</button>
        </div>
        <div class="cad-modal-body">
          ${this.generateParameterForm(gearType)}
        </div>
        <div class="cad-modal-footer">
          <button class="btn-secondary" id="btnCancelGear">Cancel</button>
          <button class="btn-primary" id="btnCreateGear">Create Gear</button>
        </div>
      </div>
    `;

    document.body.appendChild(this.modal);

    // Wire up modal controls
    this.modal.querySelector('.cad-modal-close').addEventListener('click', () => this.closeModal());
    this.modal.querySelector('#btnCancelGear').addEventListener('click', () => this.closeModal());
    this.modal.querySelector('#btnCreateGear').addEventListener('click', () => this.createGear());

    // Close on background click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Show modal with animation
    setTimeout(() => this.modal.classList.add('active'), 10);
  }

  /**
   * Generate parameter input form HTML
   */
  generateParameterForm(gearType) {
    const params = this.defaults[gearType];
    let html = '<div class="gear-params-form">';

    // Common parameters
    if (params.module !== undefined) {
      html += this.createInputField('module', 'Module', params.module, 0.1, 20, 0.1,
        'Size parameter (pitch diameter / teeth)');
    }

    if (params.teeth !== undefined) {
      html += this.createInputField('teeth', 'Number of Teeth', params.teeth, 3, 200, 1,
        'Number of gear teeth');
    }

    if (params.pressure_angle !== undefined) {
      html += this.createInputField('pressure_angle', 'Pressure Angle (°)', params.pressure_angle, 10, 30, 0.5,
        'Tooth engagement angle (typically 20°)');
    }

    if (params.width !== undefined) {
      html += this.createInputField('width', 'Width', params.width, 1, 100, 1,
        'Gear thickness');
    }

    // Type-specific parameters
    if (gearType === 'helical') {
      html += this.createInputField('helix_angle', 'Helix Angle (°)', params.helix_angle, 5, 45, 1,
        'Tooth spiral angle (15-30° typical)');
    }

    if (gearType === 'rack') {
      html += this.createInputField('height', 'Height', params.height, 1, 50, 1,
        'Rack body height');
    }

    if (gearType === 'bevel') {
      html += this.createInputField('cone_angle', 'Cone Angle (°)', params.cone_angle, 10, 80, 1,
        'Pitch cone angle (45° for right angle drive)');
    }

    if (gearType === 'worm') {
      html += this.createInputField('starts', 'Thread Starts', params.starts, 1, 4, 1,
        'Number of thread starts (typically 1)');
      html += this.createInputField('diameter', 'Pitch Diameter', params.diameter, 5, 100, 1,
        'Worm pitch diameter');
      html += this.createInputField('length', 'Length', params.length, 10, 200, 5,
        'Worm axial length');
    }

    html += '</div>';

    // Add info box with gear description
    html += `<div class="gear-info-box">${this.getGearDescription(gearType)}</div>`;

    return html;
  }

  /**
   * Create input field HTML
   */
  createInputField(name, label, value, min, max, step, tooltip) {
    return `
      <div class="gear-param-field">
        <label for="gear_${name}" title="${tooltip}">
          ${label}
          <span class="param-info" title="${tooltip}">ⓘ</span>
        </label>
        <input
          type="number"
          id="gear_${name}"
          name="${name}"
          value="${value}"
          min="${min}"
          max="${max}"
          step="${step}"
          class="gear-param-input"
        />
      </div>
    `;
  }

  /**
   * Get friendly gear type name
   */
  getGearTypeName(gearType) {
    const names = {
      'spur': 'Spur Gear',
      'helical': 'Helical Gear',
      'internal': 'Internal Gear',
      'rack': 'Rack Gear',
      'bevel': 'Bevel Gear',
      'worm': 'Worm Gear'
    };
    return names[gearType] || 'Gear';
  }

  /**
   * Get gear description
   */
  getGearDescription(gearType) {
    const descriptions = {
      'spur': 'Straight teeth parallel to axis. Simple, efficient, but can be noisy at high speeds. Used in most gear applications.',
      'helical': 'Teeth at an angle to axis. Smoother, quieter operation with higher load capacity. Creates axial thrust.',
      'internal': 'Teeth on inside of ring. Used in planetary gear systems and compact transmissions. Enables compact designs.',
      'rack': 'Linear gear with straight teeth. Converts rotary motion to linear motion. Used with pinion gears.',
      'bevel': 'Conical gear for intersecting shafts. Commonly used in differentials and right-angle drives.',
      'worm': 'Screw-like gear with high reduction ratio. Self-locking and smooth. Used for high torque at low speed.'
    };
    return descriptions[gearType] || '';
  }

  /**
   * Close modal dialog
   */
  closeModal() {
    if (this.modal) {
      this.modal.classList.remove('active');
      setTimeout(() => {
        this.modal.remove();
        this.modal = null;
      }, 300);
    }
  }

  /**
   * Create gear with current parameters
   */
  async createGear() {
    try {
      // Collect parameters from form
      const form = this.modal.querySelector('.gear-params-form');
      const inputs = form.querySelectorAll('input');

      const params = {};
      inputs.forEach(input => {
        const value = parseFloat(input.value);
        if (isNaN(value)) {
          throw new Error(`Invalid value for ${input.name}`);
        }
        params[input.name] = value;
      });

      console.log(`[GearDesigner] Creating ${this.currentGearType} gear:`, params);

      // Show loading state
      const createBtn = this.modal.querySelector('#btnCreateGear');
      const originalText = createBtn.textContent;
      createBtn.textContent = 'Creating...';
      createBtn.disabled = true;

      // Call backend to generate gear
      const gearMesh = await this.generateGearMesh(this.currentGearType, params);

      // Add to scene
      this.addGearToScene(gearMesh, this.currentGearType, params);

      // Close modal
      this.closeModal();

      // Show success message
      this.showMessage(`${this.getGearTypeName(this.currentGearType)} created successfully!`, 'success');

    } catch (error) {
      console.error('[GearDesigner] Error creating gear:', error);
      this.showMessage(`Error: ${error.message}`, 'error');

      // Reset button
      const createBtn = this.modal?.querySelector('#btnCreateGear');
      if (createBtn) {
        createBtn.textContent = 'Create Gear';
        createBtn.disabled = false;
      }
    }
  }

  /**
   * Generate gear mesh via backend API
   */
  async generateGearMesh(gearType, params) {
    // For now, create client-side using simple geometry
    // In production, this would call the backend geometry service

    // Map gear type to API endpoint
    const endpoint = `/api/cad/gear/${gearType}`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        // Fallback to client-side generation
        return this.generateGearClientSide(gearType, params);
      }

      const data = await response.json();
      return this.createMeshFromGeometryData(data.geometry);

    } catch (error) {
      console.warn('[GearDesigner] Backend unavailable, using client-side generation');
      return this.generateGearClientSide(gearType, params);
    }
  }

  /**
   * Generate gear mesh client-side (simplified)
   */
  generateGearClientSide(gearType, params) {
    // Simplified client-side gear generation
    // This creates basic cylinder approximations

    const teeth = params.teeth || 20;
    const module = params.module || 2.0;
    const width = params.width || 10.0;

    const pitchRadius = (module * teeth) / 2;
    const outerRadius = pitchRadius + module;
    const rootRadius = pitchRadius - 1.25 * module;

    // Create gear profile
    const shape = new THREE.Shape();
    const toothAngle = (2 * Math.PI) / teeth;

    for (let i = 0; i < teeth; i++) {
      const angle = i * toothAngle;
      const nextAngle = (i + 1) * toothAngle;

      // Simplified tooth profile (trapezoid)
      const toothWidth = toothAngle * 0.4;
      const rootWidth = toothAngle * 0.6;

      // Outer arc (tooth tip)
      shape.lineTo(
        Math.cos(angle - toothWidth / 2) * outerRadius,
        Math.sin(angle - toothWidth / 2) * outerRadius
      );
      shape.lineTo(
        Math.cos(angle + toothWidth / 2) * outerRadius,
        Math.sin(angle + toothWidth / 2) * outerRadius
      );

      // Root arc (between teeth)
      const midAngle = (angle + nextAngle) / 2;
      shape.lineTo(
        Math.cos(angle + rootWidth / 2) * rootRadius,
        Math.sin(angle + rootWidth / 2) * rootRadius
      );
      shape.lineTo(
        Math.cos(midAngle) * rootRadius,
        Math.sin(midAngle) * rootRadius
      );
    }

    // Close shape
    shape.closePath();

    // Extrude to 3D
    const extrudeSettings = {
      depth: width,
      bevelEnabled: false
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    // Center geometry
    geometry.translate(0, 0, -width / 2);

    const material = new THREE.MeshStandardMaterial({
      color: 0x888888,
      metalness: 0.7,
      roughness: 0.3
    });

    const mesh = new THREE.Mesh(geometry, material);

    return mesh;
  }

  /**
   * Create Three.js mesh from geometry data
   */
  createMeshFromGeometryData(data) {
    const geometry = new THREE.BufferGeometry();

    // Set attributes
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(data.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(data.normals, 3));

    if (data.indices) {
      geometry.setIndex(data.indices);
    }

    geometry.computeBoundingSphere();

    const material = new THREE.MeshStandardMaterial({
      color: 0x888888,
      metalness: 0.7,
      roughness: 0.3
    });

    const mesh = new THREE.Mesh(geometry, material);

    return mesh;
  }

  /**
   * Add gear mesh to scene
   */
  addGearToScene(mesh, gearType, params) {
    // Add to scene
    this.cadViewer.scene.add(mesh);

    // Store in CAD objects
    const featureId = `gear_${gearType}_${Date.now()}`;
    this.cadViewer.cadObjects.set(featureId, mesh);

    // Add to feature tree
    this.addToFeatureTree(featureId, gearType, params);

    // Focus camera on gear
    this.focusOnMesh(mesh);
  }

  /**
   * Add gear to feature tree
   */
  addToFeatureTree(featureId, gearType, params) {
    const featureTree = document.getElementById('cadFeatureTree');
    if (!featureTree) return;

    const featureItem = document.createElement('div');
    featureItem.className = 'cad-feature-item';
    featureItem.dataset.featureId = featureId;

    const gearName = this.getGearTypeName(gearType);
    const paramSummary = this.formatParameterSummary(gearType, params);

    featureItem.innerHTML = `
      <div class="feature-header">
        <svg class="feature-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="8"></circle>
          <circle cx="12" cy="12" r="3"></circle>
          <line x1="12" y1="2" x2="12" y2="6"></line>
          <line x1="12" y1="18" x2="12" y2="22"></line>
          <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
          <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
          <line x1="2" y1="12" x2="6" y2="12"></line>
          <line x1="18" y1="12" x2="22" y2="12"></line>
          <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
          <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
        </svg>
        <span class="feature-name">${gearName}</span>
      </div>
      <div class="feature-details">${paramSummary}</div>
    `;

    featureTree.appendChild(featureItem);

    // Wire up click handler
    featureItem.addEventListener('click', () => {
      this.selectFeature(featureId);
    });
  }

  /**
   * Format parameter summary for display
   */
  formatParameterSummary(gearType, params) {
    const parts = [];

    if (params.module) parts.push(`M${params.module}`);
    if (params.teeth) parts.push(`Z${params.teeth}`);
    if (params.width) parts.push(`W${params.width}`);

    return parts.join(' | ');
  }

  /**
   * Select feature in tree
   */
  selectFeature(featureId) {
    // Highlight in tree
    document.querySelectorAll('.cad-feature-item').forEach(item => {
      item.classList.remove('selected');
    });

    const item = document.querySelector(`[data-feature-id="${featureId}"]`);
    if (item) {
      item.classList.add('selected');
    }

    // Highlight in scene
    const mesh = this.cadViewer.cadObjects.get(featureId);
    if (mesh) {
      this.focusOnMesh(mesh);
    }
  }

  /**
   * Focus camera on mesh
   */
  focusOnMesh(mesh) {
    // Calculate bounding box
    const box = new THREE.Box3().setFromObject(mesh);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    // Move camera to view object
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = maxDim * 2;

    // Animate camera (if controls support it)
    if (this.cadViewer.controls) {
      this.cadViewer.controls.target.copy(center);
      this.cadViewer.camera.position.set(
        center.x + distance,
        center.y + distance,
        center.z + distance
      );
      this.cadViewer.controls.update();
    }
  }

  /**
   * Show message to user
   */
  showMessage(message, type = 'info') {
    // Create toast notification
    const toast = document.createElement('div');
    toast.className = `cad-toast cad-toast-${type}`;
    toast.textContent = message;

    document.body.appendChild(toast);

    // Show with animation
    setTimeout(() => toast.classList.add('active'), 10);

    // Auto-hide after 3 seconds
    setTimeout(() => {
      toast.classList.remove('active');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
}

// Export for use in main app
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GearDesigner;
}
